import React from 'react';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  icon = null,
  onClick,
  type = 'button',
  className = '',
  disabled = false,
  ...props 
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: '600',
    borderRadius: '50px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    opacity: disabled ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
    border: 'none',
    outline: 'none',
    letterSpacing: '0.3px'
  };

  const variants = {
    primary: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)',
      ':hover': {
        backgroundColor: '#2563eb',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 15px rgba(59, 130, 246, 0.4)'
      }
    },
    secondary: {
      backgroundColor: '#ffffff',
      color: '#3b82f6',
      border: '2px solid #3b82f6',
      ':hover': {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        transform: 'translateY(-2px)'
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: '#3b82f6',
      border: '2px solid #3b82f6',
      ':hover': {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        transform: 'translateY(-2px)'
      }
    },
    'outline-light': {
      backgroundColor: 'transparent',
      color: '#ffffff',
      border: '2px solid #ffffff',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-2px)'
      }
    },
    danger: {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#dc2626',
        transform: 'translateY(-2px)'
      }
    },
    success: {
      backgroundColor: '#10b981',
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#059669',
        transform: 'translateY(-2px)'
      }
    }
  };

  const sizes = {
    sm: {
      padding: '8px 16px',
      fontSize: '14px'
    },
    md: {
      padding: '12px 24px',
      fontSize: '15px'
    },
    lg: {
      padding: '14px 32px',
      fontSize: '16px'
    },
    xl: {
      padding: '16px 40px',
      fontSize: '18px'
    }
  };

  const buttonStyles = {
    ...baseStyles,
    ...variants[variant] || variants.primary,
    ...sizes[size] || sizes.md
  };

  // Handle hover effects with inline styles (since we can't use :hover in inline styles)
  const handleMouseEnter = (e) => {
    if (disabled) return;
    if (variant === 'primary') {
      e.target.style.backgroundColor = '#2563eb';
      e.target.style.transform = 'translateY(-2px)';
      e.target.style.boxShadow = '0 6px 15px rgba(59, 130, 246, 0.4)';
    } else if (variant === 'outline' || variant === 'secondary') {
      e.target.style.backgroundColor = '#3b82f6';
      e.target.style.color = '#ffffff';
      e.target.style.transform = 'translateY(-2px)';
    } else if (variant === 'outline-light') {
      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
      e.target.style.transform = 'translateY(-2px)';
    } else {
      e.target.style.transform = 'translateY(-2px)';
    }
  };

  const handleMouseLeave = (e) => {
    if (disabled) return;
    if (variant === 'primary') {
      e.target.style.backgroundColor = '#3b82f6';
      e.target.style.transform = 'translateY(0)';
      e.target.style.boxShadow = '0 4px 10px rgba(59, 130, 246, 0.3)';
    } else if (variant === 'outline' || variant === 'secondary') {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.color = '#3b82f6';
      e.target.style.transform = 'translateY(0)';
    } else if (variant === 'outline-light') {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'translateY(0)';
    } else {
      e.target.style.transform = 'translateY(0)';
    }
  };

  return (
    <button
      type={type}
      style={buttonStyles}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`button-component ${className}`}
      {...props}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  );
};

export default Button;